import { useRef, useEffect, useState } from 'react';
import backgrounds from '../../../../assets/backgrounds.jpg';
import { FaRedo } from 'react-icons/fa';
import { STRINGS } from '../../../utils/strings';

interface Card {
  title: string;
  content: string;
  bgStart: string;
  bgEnd: string;
}

interface SwipeCardCarouselProps {
  profileImage?: string;
  onIndexChange?: (index: number) => void;
  swipeMessages: Card[];
  onFirstCardUpdate?: (newContent: string) => void;
  onFirstCardReset?: () => void;
  originalFirstMessage?: string;
  currentFirstMessage?: string;
}

export default function SwipeCardCarousel({ 
  profileImage, 
  onIndexChange, 
  swipeMessages, 
  onFirstCardUpdate, 
  onFirstCardReset,
  originalFirstMessage,
  currentFirstMessage
}: SwipeCardCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const carouselRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);

  const getRandomQuestion = () => {
    const questions = STRINGS.questions;
    const randomIndex = Math.floor(Math.random() * questions.length);
    return questions[randomIndex];
  };

  const cards = swipeMessages || [];

  const handleScroll = () => {
    if (!carouselRef.current) return;
    const scrollLeft = carouselRef.current.scrollLeft;
    const child = carouselRef.current.children[0] as HTMLElement;
    const cardWidth = child.clientWidth + 16;
    const newIndex = Math.round(scrollLeft / cardWidth);
    setActiveIndex(newIndex);
    onIndexChange?.(newIndex);
  };

  const displayImage = profileImage || backgrounds;

  return (
    <>
      <div
        ref={carouselRef}
        onScroll={handleScroll}
        onMouseDown={(e) => {
          if (!carouselRef.current) return;
          isDraggingRef.current = true;
          startXRef.current = e.pageX - carouselRef.current.offsetLeft;
          scrollLeftRef.current = carouselRef.current.scrollLeft;
          carouselRef.current.style.cursor = 'grabbing';
          carouselRef.current.style.userSelect = 'none';
        }}
        onTouchStart={(e) => {
          if (!carouselRef.current) return;
          isDraggingRef.current = true;
          startXRef.current = e.touches[0].pageX - carouselRef.current.offsetLeft;
          scrollLeftRef.current = carouselRef.current.scrollLeft;
        }}
        className="w-full overflow-x-auto no-scrollbar flex gap-4 snap-x snap-mandatory px-4 pb-2 bg-transparent cursor-grab"
      >
        {cards.map((card, idx) => (
          <div
            key={idx}
            className="snap-center flex-shrink-0 rounded-3xl max-w-md w-[285px] sm:w-[240px] md:w-[280px] lg:w-full h-[210px] shadow-md overflow-hidden relative"
          >
            {idx === 0 ? (
              <>
                <div
                  className="absolute inset-0 bg-cover bg-center blur-sm opacity-80"
                  style={{
                    backgroundImage: `url(${displayImage})`,
                    filter: 'brightness(0.05)',
                  }}
                />
                <div className="relative flex flex-col items-center p-6 z-10 text-white">
                  <img
                    src={displayImage}
                    className="w-16 h-16 object-cover rounded-full border-4 border-white "
                  />
                  {isEditing ? (
                    <textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (editedContent.trim()) {
                            onFirstCardUpdate?.(editedContent.trim());
                            setIsEditing(false);
                          }
                        }
                        if (e.key === 'Escape') {
                          setIsEditing(false);
                          setEditedContent(currentFirstMessage || card.content);
                        }
                      }}
                      onBlur={() => {
                        if (editedContent.trim()) {
                          onFirstCardUpdate?.(editedContent.trim());
                        }
                        setIsEditing(false);
                      }}
                      className="text-white text-lg font-semibold text-center leading-tight mb-6 bg-transparent border-none outline-none resize-none w-full text-center"
                      rows={3}
                      autoFocus
                    />
                  ) : (
                    <h2 
                      className="text-white text-lg font-semibold text-center leading-tight mb-6 cursor-pointer hover:bg-white/10 rounded p-2"
                      onClick={() => {
                        setIsEditing(true);
                        setEditedContent(currentFirstMessage || card.content);
                      }}
                    >
                      {currentFirstMessage || card.content}
                    </h2>
                  )}
                  <div className="absolute bottom-0 right-3 flex gap-2">
                    {isEditing ? (
                      <button 
                        onClick={() => {
                          if (editedContent.trim()) {
                            onFirstCardUpdate?.(editedContent.trim());
                          }
                          setIsEditing(false);
                        }}
                        className="text-lg w-10 h-10 rounded-full bg-[rgba(255,255,255,0.25)] flex items-center justify-center hover:bg-[rgba(255,255,255,0.35)] transition-colors"
                      >
                        ✓
                      </button>
                    ) : (
                      // Only show reload button if current message differs from original
                      (currentFirstMessage || card.content) !== originalFirstMessage && (
                        <button 
                          onClick={() => onFirstCardReset?.()}
                          className="text-lg w-10 h-10 rounded-full bg-[rgba(255,255,255,0.25)] flex items-center justify-center hover:bg-[rgba(255,255,255,0.35)] transition-colors"
                        >
                          <FaRedo className="text-sm" />
                        </button>
                      )
                    )}
                    <button 
                      onClick={() => {
                        const randomQuestion = getRandomQuestion();
                        onFirstCardUpdate?.(randomQuestion);
                      }}
                      className="text-lg w-10 h-10 rounded-full bg-[rgba(255,255,255,0.25)] flex items-center justify-center hover:bg-[rgba(255,255,255,0.35)] transition-colors"
                    >
                      🎲
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div
                  className="flex flex-col justify-center items-center gap-3 p-3"
                  style={{
                    background: `linear-gradient(to bottom right, ${card.bgStart}, ${card.bgEnd})`,
                  }}
                >
                  <div className="w-12 h-12 rounded-full overflow-hidden">
                    <img
                      src={displayImage}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-3xl font-bold" style={{ fontFamily: 'revert-layer' }}>
                    {card.title}
                  </p>
                </div>
                <div className="p-4">
                  <p className="text-center text-lg font-semibold text-black">
                    {card.content}
                  </p>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Swipe indicator */}
      <div className="flex justify-center gap-2 mt-3 lg:hidden">
        {swipeMessages.map((_, idx) => (
          <div
            key={idx}
            className={`w-1 h-1 rounded-full transition-all duration-300 ${idx === activeIndex ? 'scale-110' : 'bg-gray-300'
              }`}
            style={{
              backgroundColor: idx === activeIndex ? cards[idx]?.bgStart : undefined,
            }}
          />
        ))}
      </div>
    </>
  );
}



